import { useState, useEffect, Suspense, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { useAuth } from '@/context/AuthContext';
import { api, unwrapApiResponse } from '@/utils/api';
import type { SelectOption } from '@/components/ui/Select';

import { SearchFilterBar } from '@/components/pesquisar/SearchFilterBar';
import { SearchFiltersModal, CAPITULOS_MAX_PADRAO } from '@/components/pesquisar/SearchFiltersModal';
import { SearchResultGrid } from '@/components/pesquisar/SearchResultGrid';
import Pagination from '@/components/ui/Pagination';

import { FORMATO_TODOS_SENTINEL, FORMATO_BUSCA_TODOS_QUERY } from '@/components/FormatoPicker';

/** Padrão alinhado ao FormatoPicker (Comic) e à API `obras/buscar`. */
export const FORMATO_BUSCA_PADRAO = '5';

interface Obra {
    id: number;
    nome: string;
    titulo_alternativo?: string;
    imagem: string;
    tipo?: string;
    capitulos_count?: number;
    total_capitulos?: number;
    rating?: number;
    formato?: { id: number; nome: string };
    status?: { id: number; nome: string };
}

interface FilterOption {
    id: number;
    nome: string;
}

const SORT_OPTIONS: SelectOption[] = [
    { value: 'criada_em_desc', label: 'Mais recentes' },
    { value: 'view_geral', label: 'Mais populares' },
    { value: 'nome', label: 'Ordem alfabética' },
];

export function primaryFormatoIdFromParam(formatoParam: string | null | undefined): number {
    if (!formatoParam?.trim()) return Number(FORMATO_BUSCA_PADRAO);
    const sorted = formatoParam
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => n > 0)
        .sort((a, b) => a - b)
        .join(',');
    if (sorted === FORMATO_BUSCA_TODOS_QUERY) return FORMATO_TODOS_SENTINEL;
    const first = formatoParam.split(',')[0].trim();
    const n = Number(first);
    return Number.isInteger(n) && n > 0 ? n : Number(FORMATO_BUSCA_PADRAO);
}

function processResultsData(data: unknown, page: number) {
    if (!data || typeof data !== 'object') {
        return { results: [] as Obra[], meta: { total: 0, pages: 1, page } };
    }
    const d = data as Record<string, unknown>;
    const results = Array.isArray(d.data) ? (d.data as Obra[]) : [];
    const pagination = d.pagination as Record<string, number> | undefined;
    const total = Number(pagination?.totalItems ?? d.total ?? 0);
    const limite = Number(pagination?.itemsPerPage ?? 24);
    const pages = Math.max(1, Number(pagination?.totalPages || Math.ceil(total / limite) || 1));
    return { results, meta: { total, page, pages } };
}

function SearchContent() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLoading: authLoading } = useAuth();

    const urlParams = useMemo(() => {
        const busca = searchParams.get('busca') || '';
        const formato = searchParams.get('formato') || FORMATO_BUSCA_PADRAO;
        const tags = searchParams.get('tags') ? searchParams.get('tags')!.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0) : [];
        const status = searchParams.get('status')
            ? searchParams.get('status')!.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0)
            : [];
        const minCapitulos = Number(searchParams.get('minCapitulos')) || 0;
        const maxCapitulos = Number(searchParams.get('maxCapitulos')) || CAPITULOS_MAX_PADRAO;
        const ordenarPor = searchParams.get('ordenarPor') || 'criada_em_desc';
        const pagina = Number(searchParams.get('pagina')) || 1;
        return { busca, formato, tags, status, minCapitulos, maxCapitulos, ordenarPor, pagina };
    }, [searchParams]);

    const [results, setResults] = useState<Obra[]>([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
    const [loadingResults, setLoadingResults] = useState(true);
    const [tags, setTags] = useState<FilterOption[]>([]);
    const [statuses, setStatuses] = useState<FilterOption[]>([]);
    const [loadingTags, setLoadingTags] = useState(false);

    const [searchInput, setSearchInput] = useState(urlParams.busca);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    useEffect(() => {
        setSearchInput(urlParams.busca);
    }, [urlParams.busca]);

    useEffect(() => {
        if (searchParams.get('formato') === null || searchParams.get('formato') === '') {
            const params = new URLSearchParams(searchParams.toString());
            params.set('formato', FORMATO_BUSCA_PADRAO);
            if (!params.has('pagina')) params.set('pagina', '1');
            navigate(`/pesquisar?${params.toString()}`, { replace: true });
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        const qp = new URLSearchParams();
        qp.set('limite', '24');
        qp.set('pagina', String(urlParams.pagina));
        const q = urlParams.busca.trim();
        if (q) qp.set('string', q);
        if (urlParams.formato.trim()) qp.set('formato', urlParams.formato.trim());
        if (urlParams.tags.length) qp.set('tags', urlParams.tags.join(','));
        if (urlParams.status.length) qp.set('status', urlParams.status.join(','));
        if (urlParams.minCapitulos > 0) qp.set('minCapitulos', String(urlParams.minCapitulos));
        if (urlParams.maxCapitulos !== 1000) qp.set('maxCapitulos', String(urlParams.maxCapitulos));
        if (urlParams.ordenarPor) qp.set('ordenarPor', urlParams.ordenarPor);

        let cancelled = false;
        setLoadingResults(true);
        api
            .get(`obras/buscar?${qp.toString()}`)
            .then((res) => {
                if (cancelled) return;
                const body = unwrapApiResponse<unknown>(res);
                const { results: r, meta: m } = processResultsData(body, urlParams.pagina);
                setResults(r);
                setMeta(m);
            })
            .catch(() => {
                if (!cancelled) {
                    setResults([]);
                    setMeta({ total: 0, page: urlParams.pagina, pages: 1 });
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingResults(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        urlParams.busca,
        urlParams.pagina,
        urlParams.formato,
        urlParams.tags,
        urlParams.status,
        urlParams.minCapitulos,
        urlParams.maxCapitulos,
        urlParams.ordenarPor,
    ]);

    useEffect(() => {
        void (async () => {
            try {
                const res = await api.get('obra-status');
                const data = unwrapApiResponse<unknown>(res);
                setStatuses(Array.isArray(data) ? (data as FilterOption[]) : []);
            } catch {
                setStatuses([]);
            }
        })();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchTags = async () => {
            setLoadingTags(true);
            try {
                const formatoQuery = urlParams.formato.trim() || FORMATO_BUSCA_PADRAO;
                const res = await api.get(`tags?formato=${encodeURIComponent(formatoQuery)}`);
                const data = unwrapApiResponse<unknown>(res);
                if (!cancelled) setTags(Array.isArray(data) ? (data as FilterOption[]) : []);
            } catch {
                if (!cancelled) setTags([]);
            } finally {
                if (!cancelled) setLoadingTags(false);
            }
        };
        void fetchTags();
        return () => {
            cancelled = true;
        };
    }, [urlParams.formato]);

    const updateURL = (updates: Record<string, string | number | number[] | null>) => {
        const params = new URLSearchParams(searchParams.toString());
        Object.entries(updates).forEach(([key, val]) => {
            if (val === null || val === undefined) params.delete(key);
            else if (Array.isArray(val)) {
                if (val.length === 0) params.delete(key);
                else params.set(key, val.join(','));
            } else params.set(key, String(val));
        });
        navigate(`/pesquisar?${params.toString()}`, { replace: true });
    };

    const selectedFormato = primaryFormatoIdFromParam(urlParams.formato);

    const activeFiltersCount =
        (urlParams.tags.length > 0 ? 1 : 0) +
        (urlParams.status.length > 0 ? 1 : 0) +
        (urlParams.minCapitulos > 0 || urlParams.maxCapitulos !== CAPITULOS_MAX_PADRAO ? 1 : 0) +
        (urlParams.ordenarPor !== 'criada_em_desc' ? 1 : 0);

    return (
        <div className="relative min-h-screen bg-black pb-28 selection:bg-orange-500/30 selection:text-orange-200 lg:pb-12">
            <Navbar />

            <main className="relative z-10 mx-auto max-w-7xl px-4 pb-12 pt-4 md:px-8 lg:pt-36">
                <div className="mb-8 px-2 md:mb-10">
                    <div className="flex items-center gap-4">
                        <div className="h-10 w-1.5 shrink-0 rounded-full bg-orange-600" />
                        <div>
                            <h1 className="text-2xl font-black uppercase italic tracking-tighter text-white md:text-4xl">Buscar</h1>
                            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.3em] text-white/35">Explore o catálogo</p>
                        </div>
                    </div>
                </div>

                <SearchFilterBar
                    searchInput={searchInput}
                    onSearchInputChange={setSearchInput}
                    onSearchSubmit={() => updateURL({ busca: searchInput.trim() || null, pagina: 1 })}
                    onClearSearch={() => {
                        setSearchInput('');
                        updateURL({ busca: null, pagina: 1 });
                    }}
                    selectedFormato={selectedFormato}
                    onFormatoChange={(formato) => {
                        const formatoStr =
                            formato === FORMATO_TODOS_SENTINEL ? FORMATO_BUSCA_TODOS_QUERY : String(formato);
                        updateURL({ formato: formatoStr, tags: [], pagina: 1 });
                    }}
                    onOpenFilters={() => setFilterModalOpen(true)}
                    activeFiltersCount={activeFiltersCount}
                />

                <SearchFiltersModal
                    open={filterModalOpen}
                    onClose={() => setFilterModalOpen(false)}
                    statuses={statuses}
                    selectedStatuses={urlParams.status}
                    onStatusChange={(status) => updateURL({ status, pagina: 1 })}
                    tags={tags}
                    selectedTags={urlParams.tags}
                    onTagsChange={(t) => updateURL({ tags: t, pagina: 1 })}
                    loadingTags={loadingTags}
                    ordenarPor={urlParams.ordenarPor}
                    ordenarOptions={SORT_OPTIONS}
                    onOrdenarChange={(value) => updateURL({ ordenarPor: value, pagina: 1 })}
                    minCapitulos={urlParams.minCapitulos}
                    maxCapitulos={urlParams.maxCapitulos}
                    onCapitulosChange={(min, max) =>
                        updateURL({
                            minCapitulos: min <= 0 ? null : min,
                            maxCapitulos: max === CAPITULOS_MAX_PADRAO ? null : max,
                            pagina: 1,
                        })
                    }
                    activeFiltersCount={activeFiltersCount}
                    onClearAll={() =>
                        updateURL({ status: [], tags: [], minCapitulos: null, maxCapitulos: null, ordenarPor: 'criada_em_desc', pagina: 1 })
                    }
                />

                <SearchResultGrid
                    results={results}
                    loading={loadingResults || authLoading}
                    showFormatoOnCover={selectedFormato === FORMATO_TODOS_SENTINEL}
                />

                {!loadingResults && !authLoading && results.length > 0 && meta.pages > 1 && (
                    <div className="mt-16 flex justify-center">
                        <Pagination currentPage={meta.page} totalPages={meta.pages} baseUrl="/pesquisar" />
                    </div>
                )}
            </main>
        </div>
    );
}

export default function PesquisarPage() {
    return (
        <Suspense
            fallback={
                <div className="flex min-h-screen items-center justify-center bg-black">
                    <div className="h-12 w-12 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                </div>
            }
        >
            <SearchContent />
        </Suspense>
    );
}
